import React, { useEffect, useState } from 'react';
import type { FormField } from '../interfaces';
import {
    evaluateExpression,
    expressionContainsEmptyOperand,
    findCamundaPropertyValue,
    getLocalizedLabel,
} from '../utils';
import { useFormikContext } from 'formik';
import type { FormValuesType } from './FormContainer';
import { Typography } from '@klp/design/Typography';
import { useTranslation } from 'react-i18next';
import { Alert } from '@material-ui/lab';

export const SamletRisikoSelector = ({
    field,
}: {
    field: FormField;
    isReadOnly?: boolean;
    isRequired?: boolean;
}) => {
    const { t } = useTranslation();
    const { setFieldValue, values } = useFormikContext<FormValuesType>();
    const [samletRisiko, setSamletRisiko] = useState<number | null>(null);

    const calculateRisikoPropValue = findCamundaPropertyValue(
        'calculateRisiko',
        field.properties,
    );

    useEffect(() => {
        if (!calculateRisikoPropValue) return;

        const result = evaluateExpression<number>(
            calculateRisikoPropValue,
            values,
        );
        if (result === undefined) return;

        const resultRound = +result.toFixed(2);

        setFieldValue(field.id as string, resultRound.toString());
        setSamletRisiko(resultRound);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const isAnyOperandsEmpty = expressionContainsEmptyOperand(
        values,
        calculateRisikoPropValue,
    );
    if (isAnyOperandsEmpty || samletRisiko === null) return null;

    let samletRisikoTranslation = 'leverandor.schemaRisk.low';
    let highRisk = false;
    switch (true) {
        case samletRisiko > 2 && samletRisiko < 6:
            samletRisikoTranslation = 'leverandor.schemaRisk.medium';
            break;
        case samletRisiko >= 6:
            samletRisikoTranslation = 'leverandor.schemaRisk.high';
            highRisk = true;
            break;
    }

    return (
        <div>
            <Typography>
                {getLocalizedLabel(
                    `project.formFields.${field.id}`,
                    field.label,
                    field.id,
                )}
                : {t(samletRisikoTranslation)}
            </Typography>
            {highRisk ? (
                <Alert severity="error">
                    {t('leverandor.schemaRisk.schemaWarning')}
                </Alert>
            ) : (
                <Alert severity="success">
                    {t('leverandor.schemaRisk.schemaSafe')}
                </Alert>
            )}
        </div>
    );
};
